/**
 * Store-config flags can carry per-country overrides in a companion row named
 * `<name>_by_country`: a JSON map of store country code => 0|1 (see
 * StoreConfigModel::BY_COUNTRY_SUFFIX in partners-middleware). The plain row
 * keeps its store-wide meaning and is the fallback for a country without an
 * entry, so older readers of the plain row keep working.
 */
export const BY_COUNTRY_SUFFIX = '_by_country';

export type CountryFlagMap = { [countryCode: string]: number };

/** JSON text (or an already parsed object) -> {CODE: 0|1}; anything else -> {}. */
export function parseCountryMap(raw: any): CountryFlagMap {
  let map = raw;
  if (typeof raw === 'string') {
    try {
      map = JSON.parse(raw);
    } catch {
      map = null;
    }
  }
  if (!map || typeof map !== 'object' || Array.isArray(map)) return {};

  const result: CountryFlagMap = {};
  Object.keys(map).forEach((code) => {
    result[String(code).toUpperCase()] = Number(map[code]) === 1 ? 1 : 0;
  });
  return result;
}

/**
 * Whether a flag is on for one country, given the flat name => value map the
 * `config/all` endpoint returns: the country's override when present, else
 * the plain row.
 */
export function resolveCountryFlag(configs: any, name: string, countryCode: string): boolean {
  const override = parseCountryMap(configs?.[name + BY_COUNTRY_SUFFIX])[(countryCode || '').toUpperCase()];
  if (override !== undefined) return override === 1;
  return Number(configs?.[name]) === 1;
}
